<template>
	<SectionCard>
		<template #title>
			Outcomes
		</template>
		<template #body>
			<p v-if="linkType === linkTypes.actionLink">
				Select where you would like the end user to be taken once they have used the link.
			</p>
			<p v-else>
				Select where you would like the end user to be taken once they have submitted data.
			</p>
			<Dropdown
				:value="confirmationPageTypeId"
				:items="successOutcomeOptions"
				:disabled="!userFullPermissions"
				:tooltip-text="successfulOutcomeTooltip"
				item-value="value"
				label="Successful Outcome *"
				rules="required"
				class="cassie-input-width-md"
				@input="updateSuccessOutcome('confirmationPageTypeId', $event)"
			/>
			<!-- confirmationPageTypeId -->
			<Dropdown
				v-if="confirmationPageTypeId === CONFIRMATION_PAGE_OUTCOME"
				:value="internalConfirmationPageId"
				:items="brandFilteredSuccessConfirmationPages"
				:disabled="!userFullPermissions"
				searchable
				label="Select Successful Response Page *"
				rules="required"
				class="cassie-input-width-md"
				reserve-tooltip-space
				@input="updateSuccessOutcome('internalConfirmationPageId', $event)"
			/>
			<TextField
				v-if="confirmationPageTypeId === URL_OUTCOME"
				:value="externalConfirmationPageUrl"
				:disabled="!userFullPermissions"
				label="Select Destination URL *"
				:rules="{required: true, isUrlwithHttp: true, max: 512}"
				class="cassie-input-width-md"
				reserve-tooltip-space
				@input="updateSuccessOutcome('externalConfirmationPageUrl', $event)"
			/>
			<TextField
				v-if="confirmationPageTypeId === REMAIN_ON_PAGE_OUTCOME"
				:value="successOutcome.preferenceUpdateSuccessMessage"
				:disabled="!userFullPermissions"
				label="Preference Updated Success Message"
				:rules="{max: 500}"
				class="cassie-input-width-md"
				reserve-tooltip-space
				@input="updateSuccessOutcome('preferenceUpdateSuccessMessage', $event)"
			/>
			<Dropdown
				:value="selectedFailureOutcomeOption"
				:items="failureOutcomeOptions"
				:disabled="!userFullPermissions"
				item-value="value"
				label="Failure Outcome *"
				:tooltip-text="failureOutcomeTooltip"
				rules="required"
				class="cassie-input-width-md"
				@input="handleFailureOutcomeChange"
			/>

			<Dropdown
				v-if="selectedFailureOutcomeOption === FAILURE_PAGE_OUTCOME"
				v-model="failureResponsePageId"
				:items="brandFilteredFailureConfirmationPages"
				:disabled="!userFullPermissions"
				searchable
				label="Select Failure Response Page *"
				class="cassie-input-width-md"
				reserve-tooltip-space
			/>
			<TextField
				v-if="selectedFailureOutcomeOption === FAILURE_URL_OUTCOME"
				key="failure-url-field"
				:disabled="!userFullPermissions"
				:value="customErrorUrl"
				label="Failure Destination URL *"
				class="cassie-input-width-md"
				reserve-tooltip-space
				:rules="{required: true, isUrlwithHttp: true, max: 512}"
				@input="$emit('update:customErrorUrl', $event)"
			/>
			<TextField
				v-if="selectedFailureOutcomeOption === FAILURE_REMAIN_ON_PAGE_OUTCOME"
				key="failure-message-field"
				:value="preferenceUpdateFailedMessage"
				:disabled="!userFullPermissions"
				label="Preference Update Failed Message"
				:rules="{max: 500}"
				class="cassie-input-width-md"
				reserve-tooltip-space
				@input="$emit('update:preferenceUpdateFailedMessage', $event)"
			/>
		</template>
	</SectionCard>
</template>

<script>
import SectionCard from '../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../shared/components/text-field.vue'
import { linkTypes } from './preference-links/link-types.js'

const CONFIRMATION_PAGE_OUTCOME = 2
const URL_OUTCOME = 1
const REMAIN_ON_PAGE_OUTCOME = 3
const FAILURE_URL_OUTCOME = 1
const FAILURE_PAGE_OUTCOME = 2
const FAILURE_REMAIN_ON_PAGE_OUTCOME = 3

export default {
	components: { TextField, Dropdown, SectionCard },
	props: {
		confirmationPages: Array,
		configurationOptions: Object,
		customErrorUrl: String,
		brandId: Number,
		linkType: Number,
		userFullPermissions: Boolean,
		preferenceUpdateFailedMessage: String,
		failureType: Number
	},
	setup () {
		return {
			linkTypes
		}
	},
	data () {
		return {
			CONFIRMATION_PAGE_OUTCOME,
			URL_OUTCOME,
			REMAIN_ON_PAGE_OUTCOME,
			FAILURE_PAGE_OUTCOME,
			FAILURE_URL_OUTCOME,
			FAILURE_REMAIN_ON_PAGE_OUTCOME,
			selectedSuccessOutcomeOption: null,
			selectedFailureOutcomeOption: null
		}
	},

	computed: {
		successOutcomeOptions () {
			const base = [
				{ value: CONFIRMATION_PAGE_OUTCOME, text: 'Forward to Successful Response Page' },
				{ value: URL_OUTCOME, text: 'Forward to URL' }
			]
			if (this.linkType !== linkTypes.actionLink) {
				base.push({ value: REMAIN_ON_PAGE_OUTCOME, text: 'Remain on Page' })
			}
			return base
		},
		failureOutcomeOptions () {
			const items = [
				{ value: FAILURE_URL_OUTCOME, text: 'Forward to URL' },
				{ value: FAILURE_PAGE_OUTCOME, text: 'Forward to Failure Response Page' }
			]
			if (this.linkType === linkTypes.preferencePageLink) {
				items.push({ value: FAILURE_REMAIN_ON_PAGE_OUTCOME, text: 'Remain on Page' })
			}
			return items
		},
		brandFilteredConfirmationPages () {
			if (!this.confirmationPages) {
				return []
			}
			return this.confirmationPages.filter(({ brandId }) => brandId === 0 || brandId === this.brandId)
		},
		brandFilteredSuccessConfirmationPages () {
			if (!this.confirmationPages) return []
			return this.confirmationPages
				.filter(({ brandId, confirmationPageTypeId, confirmationPageType }) => {
					const type = confirmationPageTypeId ?? confirmationPageType
					return (brandId === 0 || brandId === this.brandId) && (type === 1 || type == null)
				})
				.map(({ confirmationPageId, confirmationPageName }) => ({ value: confirmationPageId, text: confirmationPageName }))
		},
		brandFilteredFailureConfirmationPages () {
			if (!this.confirmationPages) return []
			const defaultOption = { value: null, text: '-- Forward to Default Failure Page' }
			const items = this.confirmationPages
				.filter(({ brandId, confirmationPageTypeId, confirmationPageType }) => {
					const type = confirmationPageTypeId ?? confirmationPageType
					return (brandId === 0 || brandId === this.brandId) && (type === 2 || type == null)
				})
				.map(({ confirmationPageId, confirmationPageName }) => ({ value: confirmationPageId, text: confirmationPageName }))
			return [defaultOption, ...items]
		},
		successOutcome () {
			return this.configurationOptions?.successOutcome || {}
		},
		confirmationPageTypeId () {
			return this.successOutcome?.confirmationPageTypeId ?? this.successOutcome?.confirmationPageType
		},
		externalConfirmationPageUrl () {
			return this.successOutcome?.externalConfirmationPageUrl
		},
		internalConfirmationPageId () {
			return this.successOutcome?.internalConfirmationPageId
		},
		failureResponsePageId: {
			get () {
				return this.successOutcome?.failureResponsePageId ?? null
			},
			set (value) {
				this.updateConfigurationOptions('successOutcome', { failureResponsePageId: value })
			}
		},
		successfulOutcomeTooltip () {
			switch (this.confirmationPageTypeId) {
				case CONFIRMATION_PAGE_OUTCOME:
					return 'Following a successful Preference update, the end user will be redirected to the chosen Response Page'
				case URL_OUTCOME:
					return 'Following a successful Preference update, the end user will be redirected to the Destination URL specified.  Note this must begin with http:// or https://'
				case REMAIN_ON_PAGE_OUTCOME:
					return `Following a successful Preference update the end user will remain on the page and receive an optional Preference Updated 'success' message confirming the submission has been received.`
				default:
					return 'Select the type of response if the Link update is successful'
			}
		},
		failureOutcomeTooltip () {
			switch (this.selectedFailureOutcomeOption) {
				case FAILURE_PAGE_OUTCOME:
					return 'If the update is unsuccessful, the end user will be redirected to the chosen Failure Response Page (or Default Failure Page)'
				case FAILURE_URL_OUTCOME:
					return 'If the update is unsuccessful, the end user will be redirected to the Destination URL specified.  Note this must begin with http:// or https://'
				case FAILURE_REMAIN_ON_PAGE_OUTCOME:
					return 'If the update is unsuccessful, the end user will remain on the page and receiving a plain text failure message.'
				default:
					return 'Select the type of response if the Link update fails'
			}
		}
	},
	watch: {
		failureType: {
			immediate: true,
			handler (newValue) {
				if (newValue !== undefined && newValue !== null) {
					this.selectedFailureOutcomeOption = newValue
					return
				}
				const customErrorUrl = this.customErrorUrl
				const failureMessage = this.preferenceUpdateFailedMessage
				const failurePageId = this.successOutcome?.failureResponsePageId ?? null
				let determinedValue
				if (customErrorUrl !== null && customErrorUrl !== '') {
					determinedValue = FAILURE_URL_OUTCOME
				} else if (failureMessage !== null && failureMessage !== undefined && failureMessage !== '') {
					determinedValue = FAILURE_REMAIN_ON_PAGE_OUTCOME
				} else if (failurePageId !== null) {
					determinedValue = FAILURE_PAGE_OUTCOME
				} else {
					determinedValue = FAILURE_PAGE_OUTCOME
				}
				this.selectedFailureOutcomeOption = determinedValue
				this.$nextTick(() => {
					this.$emit('update:failureType', determinedValue)
				})
			}
		}
	},
	mounted () {
		if (this.confirmationPageTypeId === CONFIRMATION_PAGE_OUTCOME) {
			this.selectedSuccessOutcomeOption = CONFIRMATION_PAGE_OUTCOME
		} else if (this.confirmationPageTypeId === URL_OUTCOME) {
			this.selectedSuccessOutcomeOption = URL_OUTCOME
		} else if (this.confirmationPageTypeId === REMAIN_ON_PAGE_OUTCOME) {
			this.selectedSuccessOutcomeOption = REMAIN_ON_PAGE_OUTCOME
		}

		// Use the failureType prop if available
		if (this.failureType !== undefined && this.failureType !== null) {
			this.selectedFailureOutcomeOption = this.failureType
		} else {
			const customErrorUrl = this.customErrorUrl
			const failureMessage = this.preferenceUpdateFailedMessage
			const failurePageId = this.successOutcome?.failureResponsePageId ?? null
			let determinedValue
			if (customErrorUrl !== null && customErrorUrl !== '') {
				determinedValue = FAILURE_URL_OUTCOME
			} else if (failureMessage !== null && failureMessage !== undefined && failureMessage !== '') {
				determinedValue = FAILURE_REMAIN_ON_PAGE_OUTCOME
			} else if (failurePageId !== null) {
				determinedValue = FAILURE_PAGE_OUTCOME
			} else {
				determinedValue = FAILURE_PAGE_OUTCOME
			}
			this.selectedFailureOutcomeOption = determinedValue
			this.$nextTick(() => {
				this.$emit('update:failureType', determinedValue)
			})
		}
	},
	methods: {
		async clearOutcomes () {
			this.updateSuccessOutcome('confirmationPageTypeId', null)
			await this.$nextTick()
			this.updateSuccessOutcome('externalConfirmationPageUrl', null)
			await this.$nextTick()
		},

		updateSuccessOutcome (property, value) {
			this.updateConfigurationOptions('successOutcome', {
				[property]: value
			})
		},

		updateConfigurationOptions (property, value) {
			const existing = this.configurationOptions || {}
			const existingProperty = existing[property] ?? {}
			const mergedProperty = {
				...existingProperty,
				...value
			}
			const merged = {
				...existing,
				[property]: mergedProperty
			}
			this.$emit('update:configurationOptions', merged)
		},

		handleFailureOutcomeChange (value) {
			this.selectedFailureOutcomeOption = value
			this.$emit('update:failureType', value)
			this.$nextTick(() => {
				if (value === FAILURE_URL_OUTCOME) {
					this.updateFailureResponsePage(null)
					this.$emit('update:preferenceUpdateFailedMessage', null)
				}	else if (value === FAILURE_PAGE_OUTCOME) {
					this.$emit('update:customErrorUrl', null)
					this.$emit('update:preferenceUpdateFailedMessage', null)
				} else if (value === FAILURE_REMAIN_ON_PAGE_OUTCOME) {
					this.updateFailureResponsePage(null)
					this.$emit('update:customErrorUrl', null)
				}
			})
		},

		updateFailureResponsePage (value) {
			this.updateConfigurationOptions('successOutcome', {
				failureResponsePageId: value
			})
		}
	}
}
</script>
